import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useSessionNoteChangeHandler from '@hooks/useSessionNoteChangeHandler';
import { useAudioTranscription } from '@hooks/useAudioTranscription';
import NoteEditor from '@components/Clinicians/Session/Components/NoteEditor';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';
import { SessionFormItem } from './SessionFormItem';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';
import { useHeidiSession } from '../hooks/useHeidiSession';
import useCallStatus from '@hooks/useCallStatus';

const UpdateNotesFormItem = () => {
  const { t } = useTranslation('common');
  const { isAudioError } = useAudioTranscription();

  const { appointment } = useSessionAppointment();
  const videoCallStatus = useCallStatus(appointment);
  const { read_only: readOnly, appointment_completed: apptCompleted, appointment_id: apptId } = appointment;

  const aiNotesAppt = useHeidiEnabled(apptId);

  const { status: aiNotesStatus, notes, hasHeidiError } = useHeidiSession();
  const handleNoteChange = useSessionNoteChangeHandler({ field: 'UPDATE' });
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (aiNotesStatus === 'completed' && notes?.hpc && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      handleNoteChange({ field: 'UPDATE', value: notes.hpc });
    }
  }, [aiNotesStatus, notes?.hpc]);

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('UPDATE');

  const getNotesVisibility = () => {
    if (!aiNotesAppt) return false;
    if (videoCallStatus === 'completed' && aiNotesStatus === 'idle') return false;
    return !isAudioError && !hasHeidiError && !apptCompleted && aiNotesStatus !== 'completed';
  };

  const shouldHideNotes = getNotesVisibility();

  return (
    <SessionFormItem
      data-testid="clinical-update"
      key="update-form-item"
      label={getNoteLabel('UPDATE')}
      required={isRequired}
      labelCol={{ span: 24 }}
      hidden={shouldHideNotes}
    >
      <NoteEditor
        data-testid="clinical-update"
        field="UPDATE"
        ariaLabel={getNoteLabel('UPDATE')}
        placeholder={t('clinicalUpdatePlaceholder')}
        readOnly={readOnly || apptCompleted}
        onChange={handleNoteChange}
      />
    </SessionFormItem>
  );
};

export default UpdateNotesFormItem;
